/**
 * Who may open each route — one declaration, used by both the navigation and
 * the route guard.
 *
 * Until now `ProtectedRoute` only asked whether you were signed in, and each
 * page decided for itself whether your role belonged there. Most did; two did
 * not. A patient who typed /administration/user-account-management was shown
 * the staff account manager, and the billing page (now /clinical-operations/billing-management) opened
 * the ADA fee reference — neither offered in their menu, both reachable.
 *
 * Ad-hoc per-page checks fail that way because nothing makes them mandatory.
 * So access is declared once, here, and enforced by one guard. Most routes take
 * their rule straight from `menuData` — the menu cannot offer a link the guard
 * refuses, because they read the same field.
 *
 * This is the second line, not the only one: the server's `requireRole` is what
 * actually protects the data. A guard here decides what a person is shown.
 */
import { menu } from './menuData.js';

/**
 * Staff workspaces that are deliberately absent from the navigation because
 * they need a patient or case in context (see menuData's notes). Being off the
 * menu is not access control — each still needs its own rule.
 */
const CONTEXTUAL_ROUTES = {
  '/clinical-operations/dentist-assessment-review': ['dentist', 'admin'],
  '/clinical-operations/treatment-plan': ['dentist', 'admin'],
  // AI transparency: a model run in full, opened from a provenance pill.
  '/ai-runs': ['dentist', 'admin'],
  '/clinical-operations/chair-side-workspace': ['dentist', 'admin'],
  '/clinical-operations/clinical-smile-review': ['dentist', 'admin'],
  '/clinical-operations/case-decision-viewer': ['dentist', 'admin'],
  '/clinical-operations/patient-manager': ['receptionist', 'dentist', 'admin'],
  '/clinical-operations/patient-profile': ['receptionist', 'dentist', 'admin'],
  '/administration/product-service-catalogue-form': ['admin'],
  '/administration/treatment-packages-form': ['admin'],

  // The patient's own journey. Staff have no business in a patient's checkout
  // or onboarding wizard — those write to whichever patient is signed in.
  '/onboarding/patient-profile-setup': ['user'],
  '/onboarding/medical-history': ['user'],
  '/onboarding/smile-goals-media-upload': ['user'],
  '/onboarding/consent-forms': ['user'],
  '/patient-care/checkout-review': ['user'],
  '/patient-care/assessment-payment': ['user'],
  '/patient-care/assessment-processing': ['user'],
  '/patient-care/consultation-checkout': ['user'],
  // 8696: the patient chooses a time the clinician offered.
  '/patient-care/appointment-invitation': ['user'],
  // 8729: the Growth & Development Program, for the parent or account holder.
  '/patient-care/growth-development': ['user'],
};

/** slug → allowed roles, for everything the app routes. */
export const routeRoles = {
  ...Object.fromEntries(
    menu
      .flatMap((group) => group.children ?? [])
      .filter((item) => Array.isArray(item.roles) && item.roles.length)
      .map((item) => [item.slug, item.roles])
  ),
  ...CONTEXTUAL_ROUTES,
};

/**
 * The roles allowed to open `pathname`, or null where no rule is declared —
 * which means "any signed-in user", the deliberate default for shared pages
 * like /account/account-management and the error pages.
 */
export function rolesForPath(pathname) {
  if (!pathname) return null;
  const path = pathname.replace(/\/+$/, '') || '/';
  if (routeRoles[path]) return routeRoles[path];
  // Nested routes (…/:id, …/form) inherit their parent's rule rather than
  // falling through to "any signed-in user" — a new sub-route is protected the
  // moment it exists, instead of when somebody remembers to list it.
  const parent = Object.keys(routeRoles)
    .filter((slug) => path.startsWith(`${slug}/`))
    .sort((a, b) => b.length - a.length)[0];
  return parent ? routeRoles[parent] : null;
}

export default { routeRoles, rolesForPath };
